import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { authedFetch, WarehouseSchema, type Warehouse } from './platformTenants';

export { WarehouseSchema, type Warehouse };

export const WarehousesListSchema = z.object({
  data: z.array(WarehouseSchema),
  total: z.number().optional(),
});
export type WarehousesList = z.infer<typeof WarehousesListSchema>;

function tenantHeaders(tenantCode: string | null, extra?: Record<string, string>): Record<string, string> {
  const h: Record<string, string> = { ...(extra ?? {}) };
  if (tenantCode) h['X-Tenant-Id'] = tenantCode;
  return h;
}

export function useTenantAdminWarehouses(token: string | null, tenantCode: string | null) {
  return useQuery({
    queryKey: ['admin', 'warehouses', tenantCode],
    queryFn: async () => WarehousesListSchema.parse(
      await authedFetch('/admin/warehouses', token, { headers: tenantHeaders(tenantCode) }),
    ),
    enabled: token !== null && tenantCode !== null,
  });
}

export interface AdminWarehouseAutoSplitFields {
  auto_split_item_count_threshold?: number | null;
  auto_split_per_row_qty_threshold?: number | null;
  auto_split_mode?: 'soft' | 'hard';
  auto_split_target_qty_per_operator?: number;
}

export function useCreateAdminWarehouse(token: string | null, tenantCode: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      body: { code: string; name: string; timezone: string; address: string | null;
        max_qty_per_mission?: number;
        sftp_inbound_folder?: string | null;
        /** SP-A — magazzino da cui ereditare schema articoli e dialog template. */
        copy_config_from_warehouse_id?: number | null } & AdminWarehouseAutoSplitFields;
      idempotencyKey: string;
    }) =>
      await authedFetch('/admin/warehouses', token, {
        method: 'POST',
        body: JSON.stringify(vars.body),
        headers: tenantHeaders(tenantCode, { 'Idempotency-Key': vars.idempotencyKey }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'warehouses'] }),
  });
}

export function useUpdateAdminWarehouse(token: string | null, tenantCode: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: number;
      patch: { name?: string; timezone?: string; address?: string | null; max_qty_per_mission?: number; sftp_inbound_folder?: string | null;
        daily_partials_mail_enabled?: boolean;
        daily_partials_mail_time?: string;
        daily_partials_mail_recipients?: string[];
      } & AdminWarehouseAutoSplitFields;
    }) =>
      await authedFetch(`/admin/warehouses/${vars.id}`, token, {
        method: 'PATCH',
        body: JSON.stringify(vars.patch),
        headers: tenantHeaders(tenantCode),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'warehouses'] }),
  });
}

/**
 * SP-A T7b — sospensione / riattivazione di un magazzino.
 *
 * Un magazzino sospeso smette di accettare liste da Book e collegamenti degli
 * operatori. È il passo che rende praticabile la sostituzione di un codice: il
 * codice è immutabile, quindi si crea un magazzino nuovo e si sospende il vecchio.
 *
 * La sospensione può fallire con 422 `warehouse_has_active_work`; la risposta
 * porta `active_missions` e `active_sessions` separati, così la UI dice COSA
 * sbloccare invece di un generico "impossibile".
 */
export interface WarehouseActiveWorkError {
  code: 'warehouse_has_active_work';
  active_missions: number;
  active_sessions: number;
}

export function useSuspendWarehouse(token: string | null, tenantCode: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: number }) =>
      await authedFetch(`/admin/warehouses/${vars.id}/suspend`, token, {
        method: 'POST',
        headers: tenantHeaders(tenantCode),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'warehouses'] }),
  });
}

export function useReactivateWarehouse(token: string | null, tenantCode: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: number }) =>
      await authedFetch(`/admin/warehouses/${vars.id}/reactivate`, token, {
        method: 'POST',
        headers: tenantHeaders(tenantCode),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'warehouses'] }),
  });
}

// ─── Ubicazioni da non prelevare ──────────────────────────────────────────────
//
// I titoli in queste ubicazioni non vengono comunicati all'operatore, ma
// restano visibili al capo magazzino e valgono come prelevati per intero verso
// Book. L'elenco è **per magazzino**: la stessa ubicazione non ha significato
// su un altro magazzino della stessa azienda.

export const ExcludedLocationSchema = z.object({
  id: z.number(),
  location_code: z.string(),
  created_at: z.string(),
});
export type ExcludedLocation = z.infer<typeof ExcludedLocationSchema>;

export function useExcludedLocations(
  token: string | null, tenantCode: string | null, warehouseId: number | null,
) {
  return useQuery({
    queryKey: ['admin', 'warehouses', warehouseId, 'excluded-locations'],
    queryFn: async () => {
      const raw = await authedFetch(
        `/admin/warehouses/${warehouseId}/excluded-locations`, token!,
        { headers: tenantHeaders(tenantCode) },
      );
      return z.object({ items: z.array(ExcludedLocationSchema) }).parse(raw).items;
    },
    enabled: token !== null && tenantCode !== null && warehouseId !== null,
  });
}

export function useAddExcludedLocation(
  token: string | null, tenantCode: string | null, warehouseId: number | null,
) {
  const qc = useQueryClient();
  return useMutation({
    // Il codice viaggia ESATTAMENTE come scritto: nessun trim, nessuna
    // maiuscola forzata. Il confronto lato server è esatto, quindi
    // normalizzarlo qui produrrebbe un elenco che non corrisponde a nulla.
    mutationFn: async (locationCode: string) => authedFetch(
      `/admin/warehouses/${warehouseId}/excluded-locations`, token!,
      { method: 'POST', headers: tenantHeaders(tenantCode),
        body: JSON.stringify({ location_code: locationCode }) },
    ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'warehouses', warehouseId, 'excluded-locations'] });
    },
  });
}

export function useRemoveExcludedLocation(
  token: string | null, tenantCode: string | null, warehouseId: number | null,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => authedFetch(
      `/admin/warehouses/${warehouseId}/excluded-locations/${id}`, token!,
      { method: 'DELETE', headers: tenantHeaders(tenantCode) },
    ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'warehouses', warehouseId, 'excluded-locations'] });
    },
  });
}
